import React from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { LayoutDashboard, User, CreditCard, Settings, LogOut, ChevronLeft, ChevronRight, X, Mail, BookOpen, Shield, FileText, Briefcase, Lock } from 'lucide-react';
import { Button } from '../ui/button';
import BrandMark from '../BrandMark';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../ui/tooltip';

// Navigation items configuration
const navItems = [
  { path: '/partner/dashboard', icon: LayoutDashboard, label: 'Dashboard' },
  { path: '/partner/wills', icon: FileText, label: 'Wills', requiresApproval: true },
  { path: '/partner/services', icon: Briefcase, label: 'Services' },
  { path: '/partner/ledger', icon: BookOpen, label: 'Ledger' },
  { path: '/partner/email-templates', icon: Mail, label: 'Email Templates' },
  { path: '/partner/consent-history', icon: Shield, label: 'Consent History' },
  { path: '/partner/profile', icon: User, label: 'Profile' },
  { path: '/partner/billing', icon: CreditCard, label: 'Billing' },
  { path: '/partner/settings', icon: Settings, label: 'Settings' },
];

const PartnerSidebar = ({
  isCollapsed,
  onToggle,
  onLogout,
  partnerStatus,
  isMobileOpen = false,
  onMobileClose = () => {},
}) => {
  const location = useLocation();
  const partnerName = localStorage.getItem('partnerName') || 'Partner';
  const isPending = partnerStatus === 'Pending';

  return (
    <aside
      className={`fixed top-0 left-0 h-screen bg-slate-900 border-r border-slate-800 flex flex-col transition-transform duration-300 md:transition-all md:duration-300
        ${isMobileOpen ? 'z-[60]' : 'z-40'}
        ${isCollapsed ? 'md:w-20' : 'md:w-64'}
        w-64
        ${isMobileOpen ? 'translate-x-0' : '-translate-x-full'}
        md:translate-x-0`}
      data-testid="partner-sidebar"
    >
      {/* Logo */}
      <div className={`h-20 flex items-center border-b border-slate-800 px-6 ${isCollapsed ? 'md:justify-center md:px-2' : ''}`}>
        <a href="/partner/dashboard" className={`flex items-center gap-3 ${isCollapsed ? 'md:justify-center md:gap-0' : ''}`}>
          <span className="md:hidden">
            <BrandMark size="md" iconOnly={false} showText={true} />
          </span>
          <span className="hidden md:flex">
            <BrandMark size="md" iconOnly={isCollapsed} showText={!isCollapsed} />
          </span>
        </a>
        {/* Mobile close button */}
        <button
          onClick={onMobileClose}
          className="md:hidden ml-auto p-2 -mr-2 rounded-lg text-slate-400 hover:text-white hover:bg-slate-800"
          aria-label="Close menu"
          data-testid="partner-sidebar-close-btn"
        >
          <X className="w-5 h-5" />
        </button>
      </div>

      {/* Partner Info — shown on mobile always, on desktop when NOT collapsed */}
      <div className={`px-6 py-4 border-b border-slate-800 ${isCollapsed ? 'md:hidden' : ''}`}>
        <p className="text-slate-400 text-xs uppercase tracking-wider">Partner Portal</p>
        <p className="text-white font-medium mt-1 truncate" title={partnerName}>{partnerName}</p>
      </div>

      {/* Toggle Button — desktop only, wrapped to prevent leak through translate-x-full */}
      <div className="hidden md:block">
        <button
          onClick={onToggle}
          className="flex absolute -right-3 top-20 w-6 h-6 bg-slate-800 border border-slate-700 rounded-full items-center justify-center text-slate-400 hover:text-white hover:bg-slate-700 transition-colors z-50"
          aria-label={isCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
        >
          {isCollapsed ? (
            <ChevronRight className="w-4 h-4" />
          ) : (
            <ChevronLeft className="w-4 h-4" />
          )}
        </button>
      </div>

      {/* Navigation */}
      <nav className={`flex-1 py-6 px-4 overflow-y-auto scrollbar-thin scrollbar-thumb-slate-700 scrollbar-track-transparent ${isCollapsed ? 'md:px-3' : ''}`}>
        <div className={`flex flex-col space-y-2 ${isCollapsed ? 'md:items-center md:space-y-3' : ''}`}>
          {navItems.map((item) => {
            const isActive = location.pathname === item.path;
            const isLocked = isPending && item.requiresApproval;
            return (
              <NavItem
                key={item.path}
                item={item}
                isCollapsed={isCollapsed}
                isActive={isActive}
                isLocked={isLocked}
              />
            );
          })}
        </div>
      </nav>

      {/* Logout */}
      <div className={`border-t border-slate-800 py-4 px-4 ${isCollapsed ? 'md:px-3 md:flex md:justify-center' : ''}`}>
        {/* Expanded button — mobile always, desktop when not collapsed */}
        <Button
          variant="ghost"
          onClick={onLogout}
          className={`w-full justify-start gap-3 px-4 py-3 h-12 text-slate-400 hover:text-red-400 hover:bg-red-500/10 rounded-xl ${isCollapsed ? 'md:hidden' : ''}`}
          data-testid="partner-logout-btn"
        >
          <LogOut className="w-5 h-5" />
          <span className="font-medium">Logout</span>
        </Button>
        {/* Icon-only button — desktop collapsed */}
        {isCollapsed && (
          <TooltipProvider delayDuration={0}>
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  onClick={onLogout}
                  className="hidden md:flex w-12 h-12 items-center justify-center rounded-xl text-slate-400 hover:text-red-400 hover:bg-red-500/10 transition-all duration-200"
                  data-testid="partner-logout-collapsed-btn"
                >
                  <LogOut className="w-5 h-5" />
                </button>
              </TooltipTrigger>
              <TooltipContent side="right" className="bg-slate-800 border-slate-700 text-white">
                Logout
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        )}
      </div>
    </aside>
  );
};

// Reusable NavItem component — renders expanded version always on mobile,
// and on desktop switches between expanded/collapsed based on isCollapsed prop.
const NavItem = ({ item, isCollapsed, isActive, isLocked }) => {
  const Icon = item.icon;

  // Expanded row (mobile always + desktop when not collapsed)
  const expandedRow = isLocked ? (
    <div
      className={`flex items-center gap-3 px-4 py-3 h-12 rounded-xl opacity-40 cursor-not-allowed text-slate-500 ${isCollapsed ? 'md:hidden' : ''}`}
      title="Available after admin approval"
    >
      <Icon className="w-5 h-5 flex-shrink-0" />
      <span className="font-medium">{item.label}</span>
      <Lock className="w-3.5 h-3.5 ml-auto" />
    </div>
  ) : (
    <NavLink
      to={item.path}
      className={`flex items-center gap-3 px-4 py-3 h-12 rounded-xl transition-all duration-200 ${
        isActive
          ? 'bg-amber-500/20 text-amber-400'
          : 'text-slate-400 hover:bg-slate-800 hover:text-white'
      } ${isCollapsed ? 'md:hidden' : ''}`}
    >
      <Icon className={`w-5 h-5 flex-shrink-0 ${isActive ? 'text-amber-400' : ''}`} />
      <span className="font-medium">{item.label}</span>
    </NavLink>
  );

  // Icon-only row (desktop-only, shown when collapsed)
  const iconRow = isCollapsed ? (
    <TooltipProvider delayDuration={0}>
      <Tooltip>
        <TooltipTrigger asChild>
          {isLocked ? (
            <div className="hidden md:flex w-12 h-12 items-center justify-center rounded-xl opacity-40 cursor-not-allowed">
              <Icon className="w-5 h-5 text-slate-500" />
            </div>
          ) : (
            <NavLink
              to={item.path}
              className={`hidden md:flex w-12 h-12 items-center justify-center rounded-xl transition-all duration-200 ${
                isActive
                  ? 'bg-amber-500/20'
                  : 'hover:bg-slate-800'
              }`}
            >
              <Icon
                className={`w-5 h-5 ${
                  isActive ? 'text-amber-400' : 'text-slate-400 hover:text-white'
                }`}
              />
            </NavLink>
          )}
        </TooltipTrigger>
        <TooltipContent side="right" className="bg-slate-800 border-slate-700 text-white">
          {isLocked ? `${item.label} — Available after admin approval` : item.label}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  ) : null;

  return (
    <>
      {expandedRow}
      {iconRow}
    </>
  );
};

export { navItems };
export default PartnerSidebar;
